import { useTranslation } from 'react-i18next';
import { Loader, Tabs, Text } from '@mantine/core';
import { useReservation } from '@/hooks/api-hooks';
import { ServiceBooking, ServiceBookingPaxGroup, ServiceOrderPaxGroup } from '@turizi/shared';
import { ServiceTitleStatic } from '@/components/services/service-title';
import { TagIcon } from '@/lib/tag-utils';
import { formatDayShort } from '@/lib/format-utils';
import { IconCalendar, IconClock, IconUsers } from '@tabler/icons-react';
import { sortServiceBookings } from '@/lib/bookings-helper';
import { ShowPaxGroup, ShowPerson } from '@/components/snippets';

interface Props {
    booking: ServiceBooking & {
        _original_booking?: ServiceBooking;
        service_order_pax_groups?: ServiceOrderPaxGroup[];
    };
}

export default function BookingPaxsBookings({ booking }: Props) {
    const { t } = useTranslation();
    const reservationId = booking.reservation_id;

    const { data: resData, isLoading } = useReservation(reservationId || 0);
    const reservation = resData?.data;

    if (!reservationId) {
        return (
            <div className="p-3 text-center text-xs text-gray-500 font-medium">
                {t('no_reservation_associated_with_booking')}
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-6 gap-2">
                <Loader size="xs" color="indigo" />
                <span className="text-xs text-gray-500 font-medium">{t('loading')}</span>
            </div>
        );
    }

    if (!reservation) {
        return (
            <div className="p-3 text-center text-xs text-red-500 font-medium">
                {t('failed_to_load_reservation')}
            </div>
        );
    }

    const bookingFromRes = reservation.service_bookings?.find(b => b.id === booking.id);
    const groupsToRender: (ServiceBookingPaxGroup | ServiceOrderPaxGroup)[] =
        (booking.service_order_pax_groups && booking.service_order_pax_groups.length > 0)
            ? booking.service_order_pax_groups
            : ((booking.pax_groups && booking.pax_groups.length > 0)
                ? booking.pax_groups
                : (bookingFromRes?.pax_groups || booking._original_booking?.pax_groups || []));

    const refPaxIds = new Set<number>();
    groupsToRender.forEach(pg => {
        const group = pg.pax_group || reservation.pax_groups?.find(rg => rg.id === pg.pax_group_id);
        group?.persons?.forEach(p => p.id && refPaxIds.add(p.id));
    });

    const refGroupIds = new Set<number>();
    groupsToRender.forEach(pg => {
        const gid = pg.pax_group_id || pg.pax_group?.id;
        if (gid) refGroupIds.add(gid);
    });

    // Find all bookings in this reservation that have ANY overlap with this booking's passengers or passenger groups, sorted by date (earliest first).
    const relatedBookings = sortServiceBookings(
        (reservation.service_bookings || []).filter(b => {
            const bPaxIds: number[] = [];
            b.pax_groups?.forEach(pg => {
                pg.pax_group?.persons?.forEach(p => p.id && bPaxIds.push(p.id));
            });
            const sharesPerson = bPaxIds.some(id => refPaxIds.has(id));
            if (sharesPerson) return true;

            const bGroupIds = b.pax_groups?.map(pg => pg.pax_group_id || pg.pax_group?.id).filter(Boolean) || [];
            return bGroupIds.some(gid => gid && refGroupIds.has(gid));
        })
    );

    return (
        <div className="p-1 min-w-[280px]">
            <Tabs defaultValue="bookings" variant="default" radius="md">
                <Tabs.List className="mb-2">
                    <Tabs.Tab
                        value="bookings"
                        leftSection={<IconCalendar size={14} />}
                        className="text-xs font-semibold py-1.5 px-2.5"
                    >
                        {t('bookings')} ({relatedBookings.length})
                    </Tabs.Tab>
                    <Tabs.Tab
                        value="paxs"
                        leftSection={<IconUsers size={14} />}
                        className="text-xs font-semibold py-1.5 px-2.5"
                    >
                        {t('paxs')} ({groupsToRender.length})
                    </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="bookings">
                    {relatedBookings.length === 0 ? null : (
                        <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                            {relatedBookings.map((b) => {
                                const isCurrent = b.id === booking.id;
                                const serviceTitle = ServiceTitleStatic(b.service_data);
                                const category = b.service_data?.category || 'other';

                                return (
                                    <div
                                        key={b.id}
                                        className={`p-2 rounded-lg border transition-all flex flex-row gap-1 ${isCurrent
                                            ? 'bg-green-50/40 border-green-400 shadow-xs'
                                            : 'bg-slate-50/40 border-slate-100'
                                            }`}
                                    >
                                        <div className="flex items-center justify-between text-[11px] text-slate-500 flex-wrap gap-2">
                                            <div className="flex items-center gap-1">
                                                <div className="text-xs font-bold text-slate-600 bg-slate-100 px-1 py-0.5 rounded">
                                                    {formatDayShort(b.start_day)}
                                                </div>
                                                <div className="flex items-center w-[50px] gap-0.5 text-slate-400">
                                                    <IconClock size={12} />
                                                    <span className="text-xs">{b.start_hour || '--:--'}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-start justify-between gap-2">
                                            <div className="flex items-center gap-1.5 min-w-0">
                                                <TagIcon size="sm" name={category} noBackground hideText />
                                                <Text size="xs" className={`truncate ${isCurrent ? 'font-bold text-indigo-900' : 'font-semibold text-slate-700'}`}>
                                                    {serviceTitle}
                                                </Text>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Tabs.Panel>

                <Tabs.Panel value="paxs">
                    {groupsToRender.length === 0 ? null : (
                        <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                            {groupsToRender.map((bg, idx) => {
                                const paxGroup = bg.pax_group || reservation.pax_groups?.find(rg => rg.id === bg.pax_group_id);
                                return (
                                    <div
                                        key={bg.id || bg.pax_group_id || idx}
                                        className="px-3 py-2 rounded-lg border border-slate-200 bg-white space-y-1"
                                    >
                                        <div className="flex items-center justify-between gap-2 flex-wrap">
                                            <span className="font-semibold text-xs text-slate-900 leading-tight">
                                                {paxGroup?.name}
                                            </span>
                                            <ShowPaxGroup
                                                infants={bg.infants}
                                                adults={bg.adults}
                                                children={bg.children}
                                                seniors={bg.seniors}
                                                guests={bg.guests}
                                            />
                                        </div>

                                        {(() => {
                                            const sbg = bg as ServiceBookingPaxGroup;
                                            const hasExplicit = Array.isArray(sbg.booking_persons) && (sbg.booking_persons.length > 0 || sbg.is_custom_config);
                                            const personsToShow = hasExplicit
                                                ? (sbg.booking_persons || [])
                                                    .map((bp) => {
                                                        const basePerson = bp.person || bp.person_data || paxGroup?.persons?.find((gp) => gp.id === bp.person_id);
                                                        if (!basePerson) return null;
                                                        return {
                                                            ...basePerson,
                                                            adults: bp.adults ?? basePerson.adults ?? 0,
                                                            children: bp.children ?? basePerson.children ?? 0,
                                                            seniors: bp.seniors ?? basePerson.seniors ?? 0,
                                                            infants: bp.infants ?? basePerson.infants ?? 0,
                                                            guests: bp.guests ?? basePerson.guests ?? 0,
                                                        };
                                                    })
                                                    .filter(Boolean)
                                                : (paxGroup?.persons || []);

                                            if (personsToShow.length === 0) return null;
                                            return (
                                                <div className="space-y-0.5 pt-0.5">
                                                    {personsToShow.map((p: any) => (
                                                        <div key={p.id} className="py-0.25">
                                                            <ShowPerson person={p} />
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        })()}

                                        {paxGroup?.observations && (
                                            <p className="text-[11px] text-slate-500 italic mt-0.5 break-words leading-tight">
                                                {paxGroup.observations}
                                            </p>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Tabs.Panel>
            </Tabs>
        </div>
    );
}
